import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_URLS, WebServiceProvider } from '@pinnakl/core/web-services';
import { CurrentUserInfoParsed, CurrentUserInfoUser, UserFromApi, UserInfoClaimsFromApi } from '@pinnakl/shared/types';
import { buildUser, getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { CurrentUserUpdatePayload } from '../current-user';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserApiService {
  private readonly apiUrls = inject(API_URLS);
  private readonly wsp = inject(WebServiceProvider);
  private readonly toast = inject(PinnaklUIToastMessage);

  async getCurrentUserInfo(accessToken?: string): Promise<CurrentUserInfoParsed> {
    try {
      return { user: await this.loadUser(accessToken) };
    } catch (e) {
      if ((e as HttpErrorResponse).status === 401) {
        throw e;
      }
      this.toast.error('Cannot load user info. ' + getErrorMessage('', e));
      throw e;
    }
  }

  async updateUserInfo(userUpdateData: CurrentUserUpdatePayload): Promise<CurrentUserInfoUser> {
    try {
      await this.updateCurrentUser(userUpdateData);
      return await this.loadUser();
    } catch (e) {
      this.toast.error('Cannot update user. ' + getErrorMessage('', e));
      throw e;
    }
  }

  async changePassword(password: string): Promise<void> {
    return this.wsp.put<void>({
      endpoint: this.apiUrls.userPasswordV4,
      payload: { password },
    });
  }

  private async loadUser(accessToken?: string): Promise<CurrentUserInfoUser> {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
    const [claims, profile] = await Promise.all([
      this.wsp.get<UserInfoClaimsFromApi>({ endpoint: this.apiUrls.authUserInfo, headers }),
      this.wsp.get<UserFromApi>({ endpoint: this.apiUrls.usersV4, headers }),
    ]);

    const clientId = Number(claims.pnkl_client_id);
    if (!Number.isInteger(clientId) || clientId <= 0) {
      throw new Error('Cannot resolve the client of the signed-in user');
    }

    return { ...buildUser(profile), clientId, userType: claims.pnkl_user_type };
  }

  private updateCurrentUser(body: CurrentUserUpdatePayload): Promise<unknown> {
    return this.wsp.put({ endpoint: this.apiUrls.usersV4, payload: body });
  }
}
